import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getJson } from '@/lib/http';
import { useYear } from '@/state/yearStore';
import { compareCyclesNewestFirst } from '@/lib/bookingCycles';
import { parseTaskDefinitionIds } from '@/lib/trackerTaskDefinitionFilter';
import type { TrackerProgressExportContext } from '@/lib/trackerProgressExport';

interface TaskDefinitionOption {
    id: number;
    label: string;
}

interface TaskDefinitionsResponse {
    cycleId: number;
    definitions: TaskDefinitionOption[];
}

export function formatOperatorTasksExportLabel(
    taskDefinitionIdsParam: string | null,
    definitions: TaskDefinitionOption[],
): string {
    if (definitions.length === 0) {
        return 'All operator tasks';
    }

    const allIds = definitions.map((definition) => definition.id);
    if (taskDefinitionIdsParam === '') {
        return 'No operator tasks';
    }

    const parsed = parseTaskDefinitionIds(taskDefinitionIdsParam);
    const selectedIds = parsed === null ? allIds : parsed.filter((id) => allIds.includes(id));

    if (selectedIds.length === 0) {
        return 'No operator tasks';
    }
    if (selectedIds.length === definitions.length) {
        return 'All operator tasks';
    }

    return definitions
        .filter((definition) => selectedIds.includes(definition.id))
        .map((definition) => definition.label)
        .join('; ');
}

export function useTrackerExportContext(input: {
    reportLabel: string;
    retailerId: string | null;
    cycleId: string | null;
    taskDefinitionIds: string | null;
}): TrackerProgressExportContext | null {
    const year = useYear();

    const { data: retailers } = useQuery<any[]>({
        queryKey: ['retailers'],
        queryFn: async () => await getJson<any[]>('/api/retailers'),
        staleTime: 30 * 60 * 1000,
    });

    const { data: cycles } = useQuery<Array<{ id: number; name: string }>>({
        queryKey: ['booking-cycles', input.retailerId, year],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (input.retailerId) params.set('retailerId', input.retailerId);
            params.set('year', String(year));
            return await getJson<Array<{ id: number; name: string }>>(`/api/booking-cycles?${params.toString()}`);
        },
        select: (data) => [...data].sort(compareCyclesNewestFirst),
        enabled: !!input.retailerId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: taskDefinitions } = useQuery<TaskDefinitionsResponse | null>({
        queryKey: ['/api/analytics/tracker/task-definitions', input.cycleId],
        queryFn: async () => {
            if (!input.cycleId) return null;
            return await getJson<TaskDefinitionsResponse>(
                `/api/analytics/tracker/task-definitions/${input.cycleId}`,
            );
        },
        enabled: !!input.cycleId,
        staleTime: 5 * 60 * 1000,
    });

    return useMemo(() => {
        if (!input.cycleId) {
            return null;
        }

        const retailerName = retailers?.find((retailer) => retailer.id.toString() === input.retailerId)?.name
            ?? 'Unknown retailer';
        const cycleName = cycles?.find((cycle) => cycle.id.toString() === input.cycleId)?.name
            ?? `Cycle ${input.cycleId}`;
        const operatorTasksLabel = formatOperatorTasksExportLabel(
            input.taskDefinitionIds,
            taskDefinitions?.definitions ?? [],
        );

        return {
            reportLabel: input.reportLabel,
            retailerName,
            cycleName,
            operatorTasksLabel,
        };
    }, [
        cycles,
        input.cycleId,
        input.reportLabel,
        input.retailerId,
        input.taskDefinitionIds,
        retailers,
        taskDefinitions?.definitions,
    ]);
}
